import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import { Typography } from 'antd'
import moment from 'moment'
import styled, { createGlobalStyle } from 'styled-components'
import { PieChart } from '@mui/x-charts'

import { icons } from 'portal/lib/primitives'
import { safeParseJSON } from 'portal/lib/functions'
import { colours } from '@qflow/theme'
import * as routes from 'portal/routes'
import { datePickerChanged } from 'portal/store/global/actions'
import { uiSetShownDeliveryTableColumns } from 'portal/store/ui/actions'
import { loadDesignMatches } from 'portal/store/home/actions'
import { useMixpanel } from 'portal/utils/mixpanel'

import { Tile, TileRow, TitleBar, TitleBarIcon } from './commonStyles'

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5rem 0;
  position: relative;
  width: 100%;
  height: 120px;

  svg {
    position: absolute;
    top: -5px; /* Adjust as needed to center the SVG */
    left: calc(50% - 68px); /* Center the SVG */
  }
`

const CenterLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.25rem;
  font-weight: 500;
  color: ${colours.DARK_GREY};
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
  z-index: 1;
`

const ChartTooltipStyles = createGlobalStyle`
  .MuiChartsTooltip-root {
    font-size: 12px !important;
    font-weight: 500 !important;
    font-family: inherit !important;
    color: ${colours.CORE_NAVY} !important;
  }

  .MuiChartsTooltip-table {
    font-size: 12px !important;
  }

  .MuiChartsTooltip-cell {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: ${colours.CORE_NAVY} !important;
  }

  .MuiChartsTooltip-labelCell {
    font-size: 12px !important;
  }

  .MuiChartsTooltip-valueCell {
    font-size: 12px !important;
    font-weight: 600 !important;
  }
`

export const DesignMatches = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()
  const { track } = useMixpanel()

  const { Title } = Typography

  useEffect(() => {
    if (accountId && projectId) {
      dispatch(loadDesignMatches(accountId, projectId))
    }
  }, [dispatch, accountId, projectId])

  const designMatches = useSelector((state: any) => state.home.designMatches)

  const data = designMatches.data

  // Calculate percentages from raw counts
  const totalProducts = data.totalProducts || 0
  const productsWithCompliantMatches = data.productsWithCompliantMatches || 0
  const productsWithFlaggedMatches = data.productsWithNeedsReviewMatches || 0

  const compliantPercentage =
    totalProducts > 0
      ? Math.round((productsWithCompliantMatches / totalProducts) * 100)
      : 0

  const flaggedPercentage =
    totalProducts > 0
      ? Math.round((productsWithFlaggedMatches / totalProducts) * 100)
      : 0

  const productsWithMatchesPercentage = compliantPercentage + flaggedPercentage
  //In case of rounding errors, ensure that productsWithoutMatchesPercentage is not negative
  const productsWithoutMatchesPercentage =
    100 - productsWithMatchesPercentage > 0
      ? 100 - productsWithMatchesPercentage
      : 0

  const handleFlaggedForReviewClick = () => {
    const startDate = '2018-01-01T00:00:00.000Z'
    const endDate = moment().endOf('day').toISOString()

    // Set localStorage item for only-flagged-design-matches-filter
    localStorage.setItem('only-flagged-design-matches-filter', 'true')

    // Ensure 'productsWithDesignMatchesCount' is in delivery-shownTableColumns in local storage and Redux State and that 'createdAt' is not.
    const shownColumnsString =
      localStorage.getItem('delivery-shownTableColumns') || '[]'
    const parsedShownColumns = safeParseJSON<string[]>(shownColumnsString, [])
    let shownColumns = Array.isArray(parsedShownColumns)
      ? parsedShownColumns
      : []

    if (!shownColumns.includes('productsWithDesignMatchesCount')) {
      shownColumns.unshift('productsWithDesignMatchesCount')
    }

    if (shownColumns.includes('createdAt')) {
      shownColumns = shownColumns.filter((col: string) => col !== 'createdAt')
    }

    dispatch(uiSetShownDeliveryTableColumns(shownColumns))
    localStorage.setItem(
      'delivery-shownTableColumns',
      JSON.stringify(shownColumns)
    )

    dispatch(
      datePickerChanged(
        {
          startDate,
          endDate
        },
        'global'
      )
    )

    if (accountId && projectId) {
      // Track the click on the tile with Mixpanel and navigate to the delivery records page with the date range applied
      track(`frontend_home_page_tile_clicked`, {
        project_id: projectId,
        account_id: accountId,
        section: 'design-matches',
        tile: 'flagged-for-review'
      })

      setTimeout(() => {
        navigate(
          generatePath(routes.DELIVERY_TABLE, {
            accountId: accountId,
            projectId: projectId
          })
        )
      }, 100)
    }
  }

  return (
    <>
      <ChartTooltipStyles />
      <TitleBar>
        <TitleBarIcon>
          <icons.Modules.DesignMatch />
        </TitleBarIcon>
        <Title level={4}>Design Matches</Title>
      </TitleBar>
      <Tiles>
        <Tile>
          <div>
            <TileRow>
              <Title level={5}>Consistent with design</Title>
            </TileRow>
            <ChartContainer>
              <PieChart
                series={[
                  {
                    data: [
                      {
                        id: 0,
                        label: 'Consistent with design',
                        value: compliantPercentage,
                        color: colours.GREEN
                      },
                      {
                        id: 1,
                        label: 'Flagged for review',
                        value: flaggedPercentage,
                        color: colours.WHITE_10
                      },
                      {
                        id: 2,
                        label: 'Products without design matches',
                        value: productsWithoutMatchesPercentage,
                        color: colours.WHITE_10
                      }
                    ],
                    innerRadius: 30,
                    outerRadius: 50,
                    paddingAngle: 2,
                    cornerRadius: 3,
                    cx: 60,
                    cy: 60,
                    valueFormatter: value => `${value.value}%`
                  }
                ]}
                slots={{
                  legend: () => null
                }}
                width={120}
                height={120}
              />
              <CenterLabel>{compliantPercentage}%</CenterLabel>
            </ChartContainer>
            <TileRow>
              Delivered products consistent with their specified requirements.
            </TileRow>
          </div>
        </Tile>

        <Tile onClick={handleFlaggedForReviewClick}>
          <div>
            <TileRow>
              <Title level={5}>Flagged for review</Title>
            </TileRow>
            <ChartContainer>
              <PieChart
                series={[
                  {
                    data: [
                      {
                        id: 0,
                        label: 'Consistent with design',
                        value: compliantPercentage,
                        color: colours.WHITE_10
                      },
                      {
                        id: 1,
                        label: 'Flagged for review',
                        value: flaggedPercentage,
                        color: colours.CONSTRUCTION_ORANGE
                      },
                      {
                        id: 2,
                        label: 'Products without design matches',
                        value: productsWithoutMatchesPercentage,
                        color: colours.WHITE_10
                      }
                    ],
                    innerRadius: 30,
                    outerRadius: 50,
                    paddingAngle: 2,
                    cornerRadius: 3,
                    cx: 60,
                    cy: 60,
                    valueFormatter: value => `${value.value}%`
                  }
                ]}
                slots={{
                  legend: () => null
                }}
                width={120}
                height={120}
              />
              <CenterLabel>{flaggedPercentage}%</CenterLabel>
            </ChartContainer>
            <TileRow>
              Delivered products that have been flagged for review.
            </TileRow>
          </div>
        </Tile>
      </Tiles>
    </>
  )
}

const Tiles = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
`
